import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { SelectableCard } from '@/components/data-display/selectable-card'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { FormSection } from '@/components/forms/form-section'
import { FormField } from '@/components/forms/form-field'
import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { Modal } from '@/components/overlays/modal'
import { Drawer } from '@/components/overlays/drawer'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Button } from '@/components/primitives/button'
import feishuIconUrl from '@/assets/im-platforms/feishu.svg'
import wecomIconUrl from '@/assets/im-platforms/wecom.svg'
import dingtalkIconUrl from '@/assets/im-platforms/dingtalk.svg'
import { ApiError } from '@/lib/api/http-client'
import { cn } from '@/lib/cn'
import { readStoredPortalAccount } from '@/lib/auth/session-storage'

import {
  deleteAdminAppImIntegration,
  disableAdminAppImIntegration,
  enableAdminAppImIntegration,
  getAdminAppImIntegrationDetail,
  getAdminAppImSetupManifest,
  getAdminAppImVerification,
  listAdminAppImPlatforms,
  listAdminAppWecomIdentityProfiles,
  upsertAdminAppImIntegration,
  validateAdminAppImIntegration,
  type AppImIntegrationDetail,
  type AppImIdentityReadiness,
  type AppImMode,
  type AppImPlatform,
  type AppImPlatformSummary,
  type AppImStatus,
  type AppImSetupManifest,
  type AppImUpsertBody,
  type AppImValidationResult,
  type AppImWecomIdentityProfileOption,
  type AppImWecomIdentityProfileDetail,
} from './im-api'
import type { AdminApp } from './api'
import { formatDateTime } from './format'

interface AdminAppImDrawerProps {
  app: AdminApp | null
  onOpenChange: (open: boolean) => void
  open: boolean
}

interface FormState {
  appId: string
  appSecret: string
  encodingAesKey: string
  groupMode: AppImMode
  groupWhitelistText: string
  p2pMode: AppImMode
  p2pWhitelistText: string
  token: string
  wecomIdentityProfileId: string
}

interface PlatformCard extends AppImPlatformSummary {
  iconSrc: string
}

type PendingNavigation = { kind: 'close' } | { kind: 'switch'; platform: AppImPlatform }

const platformOrder: AppImPlatform[] = ['feishu', 'wecom', 'dingtalk']
const platformIconSrc: Record<AppImPlatform, string> = {
  dingtalk: dingtalkIconUrl,
  feishu: feishuIconUrl,
  wecom: wecomIconUrl,
}

const modeOptions: { value: AppImMode; translationKey: string }[] = [
  { translationKey: 'imDrawer.modes.open', value: 'open' },
  { translationKey: 'imDrawer.modes.disabled', value: 'disabled' },
  { translationKey: 'imDrawer.modes.whitelist', value: 'whitelist' },
]

const emptyFormState: FormState = {
  appId: '',
  appSecret: '',
  encodingAesKey: '',
  groupMode: 'open',
  groupWhitelistText: '',
  p2pMode: 'open',
  p2pWhitelistText: '',
  token: '',
  wecomIdentityProfileId: '',
}

function supportsImAppType(appType: string) {
  return appType === 'dify' || appType === 'adp'
}

function joinIdentifiers(items?: string[]) {
  return (items ?? []).join(', ')
}

function splitIdentifiers(value: string) {
  return value
    .replace(/\n/g, ',')
    .replace(/，/g, ',')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function hydrateForm(value: AppImIntegrationDetail): FormState {
  return {
    appId: value.platform_config.app_id ?? '',
    appSecret: '',
    encodingAesKey: '',
    groupMode: value.strategy_config.group_mode,
    groupWhitelistText: joinIdentifiers(value.strategy_config.group_whitelist),
    p2pMode: value.strategy_config.p2p_mode,
    p2pWhitelistText: joinIdentifiers(value.strategy_config.p2p_whitelist),
    token: '',
    wecomIdentityProfileId: value.wecom_identity_profile_id ?? value.identity_profile?.id ?? '',
  }
}

function buildRequestBody(platform: AppImPlatform, formState: FormState): AppImUpsertBody {
  const body: AppImUpsertBody = {
    platform_config: {},
    strategy_config: {
      group_mode: formState.groupMode,
      group_whitelist: splitIdentifiers(formState.groupWhitelistText),
      p2p_mode: formState.p2pMode,
      p2p_whitelist: splitIdentifiers(formState.p2pWhitelistText),
    },
  }

  if (platform === 'feishu') {
    body.platform_config.app_id = formState.appId.trim()
    if (formState.appSecret.trim()) {
      body.platform_config.app_secret = formState.appSecret.trim()
    }
  }
  if (platform === 'wecom') {
    body.wecom_identity_profile_id = formState.wecomIdentityProfileId.trim()
    if (formState.token.trim()) {
      body.platform_config.token = formState.token.trim()
    }
    if (formState.encodingAesKey.trim()) {
      body.platform_config.encoding_aes_key = formState.encodingAesKey.trim()
    }
  }

  return body
}

function formatApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.message
  }

  return fallback
}

function platformFallback(
  platform: AppImPlatform,
  t: ReturnType<typeof useTranslation<'adminApps'>>['t'],
): PlatformCard {
  return {
    agent_platform_snapshot: null,
    configured: false,
    display_name: t(`imDrawer.platforms.${platform}.name`),
    iconSrc: platformIconSrc[platform],
    im_platform: platform,
    integration_id: null,
    status: platform === 'dingtalk' ? null : 'draft',
    supported: platform !== 'dingtalk',
    verification_status: null,
  }
}

function mergePlatforms(
  items: AppImPlatformSummary[] | undefined,
  t: ReturnType<typeof useTranslation<'adminApps'>>['t'],
) {
  const byPlatform = new Map((items ?? []).map((item) => [item.im_platform, item]))

  return platformOrder.map<PlatformCard>((platform) => ({
    ...platformFallback(platform, t),
    ...byPlatform.get(platform),
    display_name:
      byPlatform.get(platform)?.display_name ?? t(`imDrawer.platforms.${platform}.name`),
    iconSrc: platformIconSrc[platform],
  }))
}

function statusTone(status: AppImStatus | null | undefined, supported: boolean) {
  if (!supported) {
    return 'warning' as const
  }

  if (status === 'active') {
    return 'success' as const
  }

  if (status === 'disabled') {
    return 'neutral' as const
  }

  return 'info' as const
}

export function AdminAppImDrawer(props: AdminAppImDrawerProps) {
  return <KeyedAdminAppImDrawer key={props.app?.id ?? 'empty'} {...props} />
}

function KeyedAdminAppImDrawer({ app, onOpenChange, open }: AdminAppImDrawerProps) {
  const { t } = useTranslation('adminApps')
  const [selectedPlatform, setSelectedPlatform] = useState<AppImPlatform>('feishu')
  const canManageIm = app ? supportsImAppType(app.app_type) : false

  const platformsQuery = useQuery({
    enabled: open && Boolean(app?.id),
    queryFn: () => listAdminAppImPlatforms(app?.id ?? ''),
    queryKey: ['admin-app-im-platforms', app?.id],
  })

  const platformCards = useMemo(
    () => mergePlatforms(platformsQuery.data, t),
    [platformsQuery.data, t],
  )
  const selectedPlatformSummary = platformCards.find(
    (item) => item.im_platform === selectedPlatform,
  )
  const selectedPlatformSupported = Boolean(selectedPlatformSummary?.supported)

  const detailQuery = useQuery({
    enabled: open && Boolean(app?.id) && selectedPlatformSupported,
    queryFn: () => getAdminAppImIntegrationDetail(app?.id ?? '', selectedPlatform),
    queryKey: ['admin-app-im-detail', app?.id, selectedPlatform],
  })

  const manifestQuery = useQuery({
    enabled:
      open &&
      Boolean(app?.id) &&
      selectedPlatformSupported &&
      Boolean(detailQuery.data?.configured),
    queryFn: () => getAdminAppImSetupManifest(app?.id ?? '', selectedPlatform),
    queryKey: ['admin-app-im-manifest', app?.id, selectedPlatform],
  })

  const title = app?.name ? t('imDrawer.titleWithName', { name: app.name }) : t('imDrawer.title')
  const detailRevision = detailQuery.data
    ? `${selectedPlatform}-${detailQuery.data.integration_id ?? 'new'}-${detailQuery.data.status}-${detailQuery.data.verification.status}-${detailQuery.data.platform_config.app_id ?? ''}-${detailQuery.data.wecom_identity_profile_id ?? detailQuery.data.identity_profile?.id ?? ''}-${String(detailQuery.data.platform_config.has_app_secret)}-${String(detailQuery.data.platform_config.has_token)}-${String(detailQuery.data.platform_config.has_encoding_aes_key)}`
    : `${selectedPlatform}-${selectedPlatformSupported ? 'loading' : 'unsupported'}`

  return (
    <ImDrawerShell
      appId={app?.id ?? ''}
      canManageIm={canManageIm}
      detail={detailQuery.data ?? null}
      detailRevision={detailRevision}
      detailError={detailQuery.error}
      detailIsError={detailQuery.isError && selectedPlatformSupported}
      detailIsLoading={detailQuery.isLoading}
      manifest={manifestQuery.data ?? null}
      manifestError={manifestQuery.error}
      manifestIsError={manifestQuery.isError}
      manifestIsLoading={manifestQuery.isLoading}
      onOpenChange={onOpenChange}
      onRefetchDetail={() => void detailQuery.refetch()}
      onRefetchPlatforms={() => void platformsQuery.refetch()}
      onRefreshManifest={() => void manifestQuery.refetch()}
      onSelectPlatform={setSelectedPlatform}
      open={open}
      platformCards={platformCards}
      platformsError={platformsQuery.error}
      platformsIsError={platformsQuery.isError}
      platformsIsLoading={platformsQuery.isLoading}
      selectedPlatform={selectedPlatform}
      selectedPlatformSummary={selectedPlatformSummary}
      selectedPlatformSupported={selectedPlatformSupported}
      title={title}
    />
  )
}

function ImDrawerShell({
  appId,
  canManageIm,
  detail,
  detailRevision,
  detailError,
  detailIsError,
  detailIsLoading,
  manifest,
  manifestError,
  manifestIsError,
  manifestIsLoading,
  onOpenChange,
  onRefetchDetail,
  onRefetchPlatforms,
  onRefreshManifest,
  onSelectPlatform,
  open,
  platformCards,
  platformsError,
  platformsIsError,
  platformsIsLoading,
  selectedPlatform,
  selectedPlatformSummary,
  selectedPlatformSupported,
  title,
}: {
  appId: string
  canManageIm: boolean
  detail: AppImIntegrationDetail | null
  detailRevision: string
  detailError: unknown
  detailIsError: boolean
  detailIsLoading: boolean
  manifest: AppImSetupManifest | null
  manifestError: unknown
  manifestIsError: boolean
  manifestIsLoading: boolean
  onOpenChange: (open: boolean) => void
  onRefetchDetail: () => void
  onRefetchPlatforms: () => void
  onRefreshManifest: () => void
  onSelectPlatform: (platform: AppImPlatform) => void
  open: boolean
  platformCards: PlatformCard[]
  platformsError: unknown
  platformsIsError: boolean
  platformsIsLoading: boolean
  selectedPlatform: AppImPlatform
  selectedPlatformSummary: PlatformCard | undefined
  selectedPlatformSupported: boolean
  title: string
}) {
  const { t } = useTranslation('adminApps')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const [formState, setFormState] = useState<FormState>(() =>
    detail ? hydrateForm(detail) : emptyFormState,
  )
  const [savedFormState, setSavedFormState] = useState<FormState>(() =>
    detail ? hydrateForm(detail) : emptyFormState,
  )
  const [validation, setValidation] = useState<AppImValidationResult | null>(null)
  const [submitError, setSubmitError] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null)
  const [hydratedRevision, setHydratedRevision] = useState(detailRevision)
  const wecomProfilesQuery = useQuery({
    enabled: open && Boolean(appId) && selectedPlatform === 'wecom' && selectedPlatformSupported,
    queryFn: () => listAdminAppWecomIdentityProfiles(appId),
    queryKey: ['admin-app-wecom-identity-profiles', appId],
  })

  // 平台详情变化时只重置表单状态，保留 Drawer DOM，避免重播动画和滚动归零。
  if (hydratedRevision !== detailRevision) {
    const hydrated = detail?.im_platform === selectedPlatform ? hydrateForm(detail) : emptyFormState
    setHydratedRevision(detailRevision)
    setFormState(hydrated)
    setSavedFormState(hydrated)
    setValidation(null)
    setSubmitError('')
  }

  const hasUnsavedChanges =
    JSON.stringify(buildRequestBody(selectedPlatform, formState)) !==
    JSON.stringify(buildRequestBody(selectedPlatform, savedFormState))

  const currentStatus = detail?.status ?? selectedPlatformSummary?.status ?? 'draft'
  const configured = detail?.configured ?? selectedPlatformSummary?.configured ?? false
  const platformConfig = detail?.platform_config
  const hasSavedSecret =
    selectedPlatform === 'feishu'
      ? Boolean(platformConfig?.has_app_secret)
      : Boolean(platformConfig?.has_token && platformConfig.has_encoding_aes_key)
  const isEnabled = currentStatus === 'active'
  const controlsDisabled = !canManageIm || !selectedPlatformSupported

  const refreshQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-app-im-platforms', appId] }),
      queryClient.invalidateQueries({
        queryKey: ['admin-app-im-detail', appId, selectedPlatform],
      }),
    ])
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      upsertAdminAppImIntegration(
        appId,
        selectedPlatform,
        buildRequestBody(selectedPlatform, formState),
      ),
    onError: (error) => {
      setSubmitError(formatApiError(error, t('imDrawer.errors.saveFailed')))
    },
    onSuccess: async (data) => {
      setSubmitError('')
      setValidation(null)
      const hydrated = hydrateForm(data)
      setFormState(hydrated)
      setSavedFormState(hydrated)
      queryClient.setQueryData(['admin-app-im-detail', appId, selectedPlatform], data)
      showFeedback({
        message: t('imDrawer.feedback.saveSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await queryClient.invalidateQueries({ queryKey: ['admin-app-im-platforms', appId] })
      await queryClient.invalidateQueries({
        queryKey: ['admin-app-im-manifest', appId, selectedPlatform],
      })
    },
  })

  const validateMutation = useMutation({
    mutationFn: () =>
      validateAdminAppImIntegration(
        appId,
        selectedPlatform,
        buildRequestBody(selectedPlatform, formState),
      ),
    onError: (error) => {
      setValidation(null)
      setSubmitError(formatApiError(error, t('imDrawer.errors.validateFailed')))
    },
    onSuccess: (data) => {
      setSubmitError('')
      setValidation(data)
    },
  })

  const statusMutation = useMutation({
    mutationFn: (nextEnabled: boolean) =>
      nextEnabled
        ? enableAdminAppImIntegration(appId, selectedPlatform)
        : disableAdminAppImIntegration(appId, selectedPlatform),
    onError: (error) => {
      setSubmitError(formatApiError(error, t('imDrawer.errors.statusFailed')))
    },
    onSuccess: async (_data, nextEnabled) => {
      setSubmitError('')
      showFeedback({
        message: nextEnabled
          ? t('imDrawer.feedback.enableSuccess')
          : t('imDrawer.feedback.disableSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await refreshQueries()
    },
  })

  const verificationMutation = useMutation({
    mutationFn: () => getAdminAppImVerification(appId, selectedPlatform),
    onError: (error) => {
      setSubmitError(formatApiError(error, t('imDrawer.errors.verificationFailed')))
    },
    onSuccess: async (verification) => {
      setSubmitError('')
      if (detail) {
        queryClient.setQueryData(['admin-app-im-detail', appId, selectedPlatform], {
          ...detail,
          verification,
        })
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-app-im-platforms', appId] }),
        queryClient.invalidateQueries({
          queryKey: ['admin-app-im-manifest', appId, selectedPlatform],
        }),
      ])
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminAppImIntegration(appId, selectedPlatform),
    onError: (error) => {
      setSubmitError(formatApiError(error, t('imDrawer.errors.deleteFailed')))
    },
    onSuccess: async () => {
      setDeleteDialogOpen(false)
      setFormState({ ...emptyFormState })
      setSavedFormState({ ...emptyFormState })
      setValidation(null)
      setSubmitError('')
      showFeedback({
        message: t('imDrawer.feedback.clearSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await refreshQueries()
    },
  })

  const isWorking =
    saveMutation.isPending ||
    validateMutation.isPending ||
    statusMutation.isPending ||
    verificationMutation.isPending ||
    deleteMutation.isPending

  const completeNavigation = (navigation: PendingNavigation) => {
    setPendingNavigation(null)
    if (navigation.kind === 'switch') {
      onSelectPlatform(navigation.platform)
      return
    }
    onOpenChange(false)
  }

  const requestNavigation = (navigation: PendingNavigation) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(navigation)
      return
    }
    completeNavigation(navigation)
  }

  return (
    <>
      <Drawer
        footer={
          <>
            <Button
              onClick={() => requestNavigation({ kind: 'close' })}
              size="md"
              variant="secondary"
            >
              {t('imDrawer.cancel')}
            </Button>
            <Button
              disabled={
                controlsDisabled ||
                detailIsLoading ||
                (selectedPlatform === 'wecom' && !formState.wecomIdentityProfileId)
              }
              isLoading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              size="md"
            >
              {t('imDrawer.save')}
            </Button>
          </>
        }
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            onOpenChange(true)
            return
          }
          requestNavigation({ kind: 'close' })
        }}
        open={open}
        size="lg"
        title={title}
      >
        <div className="space-y-6">
          {platformsIsError ? (
            <ErrorState
              action={
                <Button onClick={onRefetchPlatforms} size="sm" variant="secondary">
                  {t('retry')}
                </Button>
              }
              description={formatApiError(platformsError, t('imDrawer.errors.loadFailed'))}
              title={t('imDrawer.errors.loadFailedTitle')}
            />
          ) : (
            <>
              {!canManageIm ? <UnsupportedAppNotice /> : null}

              <PlatformCards
                isLoading={platformsIsLoading}
                onSelect={(platform) => {
                  if (platform !== selectedPlatform) {
                    requestNavigation({ kind: 'switch', platform })
                  }
                }}
                platforms={platformCards}
                selectedPlatform={selectedPlatform}
              />

              {detailIsError ? (
                <ErrorState
                  action={
                    <Button onClick={onRefetchDetail} size="sm" variant="secondary">
                      {t('retry')}
                    </Button>
                  }
                  description={formatApiError(detailError, t('imDrawer.errors.detailFailed'))}
                  title={t('imDrawer.errors.detailFailedTitle')}
                />
              ) : (
                <ImConfigurationContent
                  configured={configured}
                  controlsDisabled={controlsDisabled || detailIsLoading}
                  currentStatus={currentStatus}
                  detail={detail}
                  formState={formState}
                  hasSavedSecret={hasSavedSecret}
                  isEnabled={isEnabled}
                  isWorking={isWorking}
                  manifest={manifest}
                  manifestError={manifestError}
                  manifestIsError={manifestIsError}
                  manifestIsLoading={manifestIsLoading}
                  onClear={() => setDeleteDialogOpen(true)}
                  onCopyCallback={async () => {
                    if (!manifest?.callback_url) return
                    try {
                      await navigator.clipboard.writeText(manifest.callback_url)
                      showFeedback({
                        message: t('imDrawer.feedback.copySuccess'),
                        scope: 'global',
                        tone: 'success',
                      })
                    } catch {
                      showFeedback({
                        message: t('imDrawer.errors.copyFailed'),
                        scope: 'global',
                        tone: 'error',
                      })
                    }
                  }}
                  onRefreshManifest={onRefreshManifest}
                  onRefreshVerification={() => verificationMutation.mutate()}
                  onStatusChange={(nextEnabled) => statusMutation.mutate(nextEnabled)}
                  onUpdateFormState={(key, value) => {
                    setValidation(null)
                    setFormState((current) => ({ ...current, [key]: value }))
                  }}
                  onValidate={() => validateMutation.mutate()}
                  selectedPlatform={selectedPlatform}
                  selectedPlatformSummary={selectedPlatformSummary}
                  submitError={submitError}
                  validation={validation}
                  wecomProfiles={wecomProfilesQuery.data ?? []}
                  wecomProfilesError={wecomProfilesQuery.error}
                  wecomProfilesIsError={wecomProfilesQuery.isError}
                  wecomProfilesIsLoading={wecomProfilesQuery.isLoading}
                />
              )}
            </>
          )}
        </div>
      </Drawer>

      <Modal
        closeLabel={t('imDrawer.deleteDialog.close')}
        footer={
          <>
            <Button onClick={() => setDeleteDialogOpen(false)} size="md" variant="secondary">
              {t('imDrawer.deleteDialog.cancel')}
            </Button>
            <Button
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              size="md"
              variant="danger"
            >
              {t('imDrawer.deleteDialog.confirm')}
            </Button>
          </>
        }
        onClose={() => setDeleteDialogOpen(false)}
        open={deleteDialogOpen}
        subtitle={t('imDrawer.deleteDialog.subtitle')}
        title={t('imDrawer.deleteDialog.title')}
      >
        <p className="text-sm leading-6 text-[var(--color-text-muted)]">
          {t('imDrawer.deleteDialog.message')}
        </p>
      </Modal>

      <Modal
        closeLabel={t('imDrawer.unsavedDialog.continueEditing')}
        footer={
          <>
            <Button onClick={() => setPendingNavigation(null)} size="md" variant="secondary">
              {t('imDrawer.unsavedDialog.continueEditing')}
            </Button>
            <Button
              onClick={() => {
                if (pendingNavigation) {
                  completeNavigation(pendingNavigation)
                }
              }}
              size="md"
              variant="secondary"
            >
              {t('imDrawer.unsavedDialog.discard')}
            </Button>
            <Button
              isLoading={saveMutation.isPending}
              onClick={() => {
                if (!pendingNavigation) return
                const navigation = pendingNavigation
                saveMutation.mutate(undefined, {
                  onSuccess: () => completeNavigation(navigation),
                })
              }}
              size="md"
            >
              {t('imDrawer.unsavedDialog.save')}
            </Button>
          </>
        }
        onClose={() => setPendingNavigation(null)}
        open={pendingNavigation !== null}
        subtitle={t('imDrawer.unsavedDialog.subtitle')}
        title={t('imDrawer.unsavedDialog.title')}
      >
        <p className="text-sm leading-6 text-[var(--color-text-muted)]">
          {t('imDrawer.unsavedDialog.message')}
        </p>
      </Modal>
    </>
  )
}

function UnsupportedAppNotice() {
  const { t } = useTranslation('adminApps')

  return <InlineNotice tone="warning">{t('imDrawer.unsupportedAppType')}</InlineNotice>
}

function PlatformCards({
  isLoading,
  onSelect,
  platforms,
  selectedPlatform,
}: {
  isLoading: boolean
  onSelect: (platform: AppImPlatform) => void
  platforms: PlatformCard[]
  selectedPlatform: AppImPlatform
}) {
  const { t } = useTranslation('adminApps')

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {platforms.map((platform) => {
        const selected = selectedPlatform === platform.im_platform
        const unavailable = !platform.supported
        const statusLabel = unavailable
          ? t('imDrawer.status.comingSoon')
          : platform.configured
            ? t(`imDrawer.status.${platform.status ?? 'draft'}`)
            : t('imDrawer.status.unconfigured')

        return (
          <SelectableCard
            as="button"
            aria-pressed={selected}
            className={cn(
              'min-h-20 rounded-xl px-3 py-3 text-left',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
              unavailable ? 'opacity-80' : null,
            )}
            disabled={unavailable || isLoading}
            key={platform.im_platform}
            onClick={() => onSelect(platform.im_platform)}
            selected={selected}
            selectedClassName="border-[var(--color-primary)] shadow-[0_0_0_1px_var(--color-primary-soft)]"
            unselectedClassName="border-[var(--color-control-border)] hover:border-[var(--color-primary)]"
          >
            <span className="flex items-start justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <img
                  alt=""
                  className="size-[18px] shrink-0 object-contain"
                  data-testid={`im-platform-logo-${platform.im_platform}`}
                  src={platform.iconSrc}
                />
                <span className="truncate text-sm font-bold text-[var(--color-text)]">
                  {platform.display_name}
                </span>
              </span>
              <Badge
                className="h-5 shrink-0 rounded-none bg-transparent px-0"
                variant={statusTone(platform.status, platform.supported)}
              >
                {statusLabel}
              </Badge>
            </span>
            <span className="mt-2 block truncate text-xs text-[var(--color-text-subtle)]">
              {unavailable
                ? t('imDrawer.platformUnavailableDescription')
                : platform.configured
                  ? t('imDrawer.platformConfiguredDescription')
                  : t('imDrawer.platformUnconfiguredDescription')}
            </span>
          </SelectableCard>
        )
      })}
    </div>
  )
}

function ImConfigurationContent({
  configured,
  controlsDisabled,
  currentStatus,
  detail,
  formState,
  hasSavedSecret,
  isEnabled,
  isWorking,
  manifest,
  manifestError,
  manifestIsError,
  manifestIsLoading,
  onClear,
  onCopyCallback,
  onRefreshManifest,
  onRefreshVerification,
  onStatusChange,
  onUpdateFormState,
  onValidate,
  selectedPlatform,
  selectedPlatformSummary,
  submitError,
  validation,
  wecomProfiles,
  wecomProfilesError,
  wecomProfilesIsError,
  wecomProfilesIsLoading,
}: {
  configured: boolean
  controlsDisabled: boolean
  currentStatus: AppImStatus | null
  detail: AppImIntegrationDetail | null
  formState: FormState
  hasSavedSecret: boolean
  isEnabled: boolean
  isWorking: boolean
  manifest: AppImSetupManifest | null
  manifestError: unknown
  manifestIsError: boolean
  manifestIsLoading: boolean
  onClear: () => void
  onCopyCallback: () => void
  onRefreshManifest: () => void
  onRefreshVerification: () => void
  onStatusChange: (checked: boolean) => void
  onUpdateFormState: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  onValidate: () => void
  selectedPlatform: AppImPlatform
  selectedPlatformSummary: PlatformCard | undefined
  submitError: string
  validation: AppImValidationResult | null
  wecomProfiles: AppImWecomIdentityProfileOption[]
  wecomProfilesError: unknown
  wecomProfilesIsError: boolean
  wecomProfilesIsLoading: boolean
}) {
  const { t } = useTranslation('adminApps')
  const platformName =
    selectedPlatformSummary?.display_name ?? t(`imDrawer.platforms.${selectedPlatform}.name`)
  const verification = detail?.verification
  const isVerified =
    verification?.status === 'verified' &&
    verification.verified_credential_version === verification.credential_version
  const identityReadiness = manifest?.identity_readiness ?? null
  const callbackReadiness = manifest?.callback_readiness ?? null
  const aggregateReadiness = manifest?.readiness ?? 'unknown'
  const wecomEnableBlocker =
    selectedPlatform === 'wecom' && aggregateReadiness !== 'ready'
      ? resolveWecomEnableBlocker(t, identityReadiness, callbackReadiness, aggregateReadiness)
      : null

  return (
    <div className="space-y-6">
      {submitError ? (
        <InlineNotice role="alert" tone="danger">
          {submitError}
        </InlineNotice>
      ) : null}

      {selectedPlatform === 'wecom' ? (
        <>
          <SectionTitle>{t('imDrawer.sections.wecomIdentityProfile')}</SectionTitle>
          <FormSection
            className="p-6"
            title={t('imDrawer.wecomProfile.title')}
            titleExtra={<SectionHelp content={t('imDrawer.wecomProfile.description')} />}
          >
            <WecomIdentityProfileSelect
              currentProfile={detail?.identity_profile ?? null}
              disabled={controlsDisabled || isEnabled}
              error={wecomProfilesError}
              formState={formState}
              isError={wecomProfilesIsError}
              isLoading={wecomProfilesIsLoading}
              onChange={(value) => onUpdateFormState('wecomIdentityProfileId', value)}
              profiles={wecomProfiles}
            />
          </FormSection>
        </>
      ) : null}

      <SectionTitle>
        {selectedPlatform === 'wecom'
          ? t('imDrawer.sections.wecomSecurity')
          : t('imDrawer.sections.dataConfig')}
      </SectionTitle>
      <FormSection
        action={
          <div className="flex gap-2">
            <Button
              className="border-[#ffb4b4] text-[#ff8c8c] hover:bg-[#fff1f0]"
              disabled={controlsDisabled || !configured}
              onClick={onClear}
              size="sm"
              variant="secondary"
            >
              {t('imDrawer.clear')}
            </Button>
            <Button
              disabled={
                controlsDisabled ||
                isWorking ||
                (selectedPlatform === 'wecom' && !formState.wecomIdentityProfileId)
              }
              onClick={onValidate}
              size="sm"
              variant="secondary"
            >
              {t('imDrawer.validate')}
            </Button>
          </div>
        }
        className="p-6"
        title={t('imDrawer.credentials.title', { platform: platformName })}
        titleExtra={
          <HelpTooltip
            label={`${t(`imDrawer.credentials.${selectedPlatform}.secretHint`)}${
              hasSavedSecret ? ` ${t('imDrawer.credentials.savedSecret')}` : ''
            }`}
          >
            <span className="grid gap-1">
              <span>{t(`imDrawer.credentials.${selectedPlatform}.secretHint`)}</span>
              {hasSavedSecret ? <span>{t('imDrawer.credentials.savedSecret')}</span> : null}
            </span>
          </HelpTooltip>
        }
      >
        <div className="grid gap-4">
          {selectedPlatform === 'feishu' ? (
            <>
              <SecretField
                disabled={controlsDisabled}
                label={t('imDrawer.credentials.feishu.appId')}
                onChange={(value) => onUpdateFormState('appId', value)}
                placeholder={t('imDrawer.credentials.feishu.appIdPlaceholder')}
                secret={false}
                value={formState.appId}
              />
              <SecretField
                disabled={controlsDisabled}
                label={t('imDrawer.credentials.feishu.appSecret')}
                onChange={(value) => onUpdateFormState('appSecret', value)}
                placeholder={
                  hasSavedSecret
                    ? t('imDrawer.credentials.preservePlaceholder')
                    : t('imDrawer.credentials.feishu.appSecretPlaceholder')
                }
                value={formState.appSecret}
              />
            </>
          ) : (
            <>
              <SecretField
                disabled={controlsDisabled}
                label={t('imDrawer.credentials.wecom.token')}
                onChange={(value) => onUpdateFormState('token', value)}
                placeholder={
                  detail?.platform_config.has_token
                    ? t('imDrawer.credentials.preservePlaceholder')
                    : t('imDrawer.credentials.wecom.tokenPlaceholder')
                }
                value={formState.token}
              />
              <SecretField
                disabled={controlsDisabled}
                label={t('imDrawer.credentials.wecom.encodingAesKey')}
                onChange={(value) => onUpdateFormState('encodingAesKey', value)}
                placeholder={
                  detail?.platform_config.has_encoding_aes_key
                    ? t('imDrawer.credentials.preservePlaceholder')
                    : t('imDrawer.credentials.wecom.encodingAesKeyPlaceholder')
                }
                value={formState.encodingAesKey}
              />
            </>
          )}
        </div>
        {selectedPlatform === 'feishu' && detail?.generated_secrets ? (
          <InlineNotice tone="warning">
            <div className="grid gap-3">
              <strong>{t('imDrawer.credentials.feishu.generatedTitle')}</strong>
              <p>{t('imDrawer.credentials.feishu.generatedDescription')}</p>
              {detail.generated_secrets.encrypt_key ? (
                <CopyableReadonlyField
                  copyLabel={t('imDrawer.credentials.copyEncryptKey')}
                  label={t('imDrawer.credentials.feishu.encryptKey')}
                  monospace
                  onCopy={() =>
                    void navigator.clipboard.writeText(detail.generated_secrets?.encrypt_key ?? '')
                  }
                  showLabel
                  value={detail.generated_secrets.encrypt_key}
                />
              ) : null}
              {detail.generated_secrets.verification_token ? (
                <CopyableReadonlyField
                  copyLabel={t('imDrawer.credentials.copyVerificationToken')}
                  label={t('imDrawer.credentials.feishu.verificationToken')}
                  monospace
                  onCopy={() =>
                    void navigator.clipboard.writeText(
                      detail.generated_secrets?.verification_token ?? '',
                    )
                  }
                  showLabel
                  value={detail.generated_secrets.verification_token}
                />
              ) : null}
            </div>
          </InlineNotice>
        ) : null}
      </FormSection>

      {validation ? <ValidationResult platform={selectedPlatform} validation={validation} /> : null}

      <SectionTitle help={t(`imDrawer.setupGuide.${selectedPlatform}.description`)}>
        {t('imDrawer.sections.statusAndGuide')}
      </SectionTitle>
      <FormSection className="p-6">
        <div className="grid gap-4 border-b border-[var(--color-divider)] pb-5">
          <div className="grid gap-1 text-sm text-[#374151]">
            <span>{t('imDrawer.setupGuide.callbackUrl')}</span>
            {manifestIsError ? (
              <InlineNotice tone="danger">
                {formatApiError(manifestError, t('imDrawer.errors.manifestFailed'))}
              </InlineNotice>
            ) : manifest?.callback_url ? (
              <CopyableReadonlyField
                copyLabel={t('imDrawer.setupGuide.copyCallback')}
                label={t('imDrawer.setupGuide.callbackUrl')}
                monospace
                onCopy={onCopyCallback}
                value={manifest.callback_url}
              />
            ) : (
              <p className="text-xs text-[var(--color-text-subtle)]">
                {manifestIsLoading
                  ? t('imDrawer.setupGuide.loading')
                  : t('imDrawer.setupGuide.saveFirst')}
              </p>
            )}
          </div>
          {selectedPlatform === 'wecom' ? (
            <div className="text-xs text-[var(--color-text-subtle)]">
              {t('imDrawer.setupGuide.wecom.botId')}:{' '}
              {detail?.platform_config.aibotid ?? t('imDrawer.setupGuide.wecom.botIdUnknown')}
            </div>
          ) : null}
        </div>
        <div className="flex min-h-[56px] items-center justify-between gap-6 border-b border-[var(--color-divider)] py-4">
          <div className="min-w-0">
            <div className="text-sm text-[#374151]">{t('imDrawer.verification.title')}</div>
            <div className="mt-1 text-xs text-[var(--color-text-subtle)]">
              {t(`imDrawer.verification.status.${verification?.status ?? 'unverified'}`)}
              {' · '}
              {verification?.last_verified_at
                ? t('imDrawer.verification.lastVerified', {
                    time: formatDateTime(verification.last_verified_at),
                  })
                : t('imDrawer.verification.neverVerified')}
            </div>
          </div>
          <Button
            disabled={!configured || isWorking}
            onClick={() => {
              onRefreshVerification()
              onRefreshManifest()
            }}
            size="sm"
            variant="secondary"
          >
            {t('imDrawer.verification.refresh')}
          </Button>
        </div>
        {selectedPlatform !== 'wecom' ? (
          <IdentityReadinessPanel isLoading={manifestIsLoading} readiness={identityReadiness} />
        ) : null}
        <div className="flex min-h-[52px] items-center justify-between gap-6 pt-4">
          <div className="min-w-0">
            <div className="text-sm text-[#374151]">{t('imDrawer.appStatus.title')}</div>
            <div className="mt-1 text-xs text-[var(--color-text-subtle)]">
              {!isVerified
                ? t('imDrawer.appStatus.verificationRequired')
                : (wecomEnableBlocker ??
                  (identityReadiness?.status === 'blocked'
                    ? t('imDrawer.appStatus.identityBlocked')
                    : identityReadiness?.status === 'unknown'
                      ? t('imDrawer.appStatus.identityUnknown')
                      : t('imDrawer.appStatus.description')))}
            </div>
          </div>
          <Switch
            checked={isEnabled}
            disabled={
              controlsDisabled ||
              !configured ||
              !isVerified ||
              isWorking ||
              (!isEnabled && selectedPlatform === 'wecom' && aggregateReadiness !== 'ready')
            }
            onCheckedChange={onStatusChange}
          />
        </div>
      </FormSection>

      <SectionTitle help={t(`imDrawer.behavior.${selectedPlatform}.description`)}>
        {t('imDrawer.sections.behavior')}
      </SectionTitle>
      <FormSection className="p-6">
        <ModeRadioRow
          disabled={controlsDisabled}
          name="p2p-mode"
          onChange={(value) => onUpdateFormState('p2pMode', value)}
          title={t('imDrawer.behavior.p2p')}
          value={formState.p2pMode}
        />
        {formState.p2pMode === 'whitelist' ? (
          <WhitelistTextarea
            disabled={controlsDisabled}
            label={t('imDrawer.behavior.p2pWhitelist')}
            onChange={(value) => onUpdateFormState('p2pWhitelistText', value)}
            placeholder={t(`imDrawer.behavior.${selectedPlatform}.p2pWhitelistPlaceholder`)}
            value={formState.p2pWhitelistText}
          />
        ) : null}
        <div className="my-4 h-px bg-[var(--color-divider)]" />
        <ModeRadioRow
          disabled={controlsDisabled}
          name="group-mode"
          onChange={(value) => onUpdateFormState('groupMode', value)}
          title={t('imDrawer.behavior.group')}
          value={formState.groupMode}
        />
        {formState.groupMode === 'whitelist' ? (
          <WhitelistTextarea
            disabled={controlsDisabled}
            label={t('imDrawer.behavior.groupWhitelist')}
            onChange={(value) => onUpdateFormState('groupWhitelistText', value)}
            placeholder={t(`imDrawer.behavior.${selectedPlatform}.groupWhitelistPlaceholder`)}
            value={formState.groupWhitelistText}
          />
        ) : null}
      </FormSection>

      {/* 未配置时不能复用 draft 文案，否则读屏播报「未启用」与卡片上的「未配置」不一致。 */}
      <span className="sr-only">
        {configured
          ? t(`imDrawer.status.${currentStatus ?? 'draft'}`)
          : t('imDrawer.status.unconfigured')}
      </span>
    </div>
  )
}

function SecretField({
  disabled,
  label,
  onChange,
  placeholder,
  secret = true,
  value,
}: {
  disabled: boolean
  label: string
  onChange: (value: string) => void
  placeholder: string
  secret?: boolean
  value: string
}) {
  return (
    <label className="grid gap-2">
      <span className="text-sm text-[#374151]">
        {label}
        <span className="text-[var(--color-danger)]"> *</span>
      </span>
      <Input
        aria-label={label}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type={secret ? 'password' : 'text'}
        value={value}
      />
    </label>
  )
}

function WecomIdentityProfileSelect({
  currentProfile,
  disabled,
  error,
  formState,
  isError,
  isLoading,
  onChange,
  profiles,
}: {
  currentProfile: AppImWecomIdentityProfileDetail | null
  disabled: boolean
  error: unknown
  formState: FormState
  isError: boolean
  isLoading: boolean
  onChange: (value: string) => void
  profiles: AppImWecomIdentityProfileOption[]
}) {
  const { t } = useTranslation('adminApps')
  const isSystemAdmin = readStoredPortalAccount('admin').role === 'super_admin'
  const profilesWithCurrent =
    currentProfile?.id && !profiles.some((item) => item.id === currentProfile.id)
      ? [currentProfile, ...profiles]
      : profiles
  const selectedProfile = profilesWithCurrent.find(
    (item) => item.id === formState.wecomIdentityProfileId,
  )
  const selectedProfileUnavailable = Boolean(selectedProfile && selectedProfile.status !== 'active')
  const options = [
    { label: t('imDrawer.wecomProfile.placeholder'), value: '' },
    ...profilesWithCurrent.map((profile) => ({
      label: profile.display_name,
      value: profile.id,
    })),
  ]

  return (
    <div className="grid gap-3">
      {isError ? (
        <InlineNotice tone="danger">
          {formatApiError(error, t('imDrawer.wecomProfile.loadFailed'))}
        </InlineNotice>
      ) : null}
      <FormField
        hint={disabled ? t('imDrawer.wecomProfile.activeReadonly') : undefined}
        htmlFor="wecom-identity-profile-select"
        label={t('imDrawer.wecomProfile.field')}
        required
      >
        <Select
          aria-label={t('imDrawer.wecomProfile.field')}
          disabled={disabled || isLoading || isError}
          id="wecom-identity-profile-select"
          onValueChange={onChange}
          options={options}
          value={formState.wecomIdentityProfileId}
        />
      </FormField>
      {!isLoading && !isError && profiles.length === 0 ? (
        <InlineNotice tone="warning">
          <p>
            {isSystemAdmin
              ? t('imDrawer.wecomProfile.emptySystemAdmin')
              : t('imDrawer.wecomProfile.emptyAppAdmin')}
          </p>
          {isSystemAdmin ? (
            <a className="mt-1 inline-flex font-medium underline" href="/admin/credentials/wecom">
              {t('imDrawer.wecomProfile.manage')}
            </a>
          ) : null}
        </InlineNotice>
      ) : null}
      {selectedProfile?.readiness.status === 'ready' ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
          <Badge variant="success">{t('imDrawer.wecomProfile.available')}</Badge>
        </div>
      ) : selectedProfile &&
        (selectedProfile.readiness.status === 'blocked' || selectedProfileUnavailable) ? (
        <InlineNotice tone="danger">{t('imDrawer.wecomProfile.unavailable')}</InlineNotice>
      ) : null}
    </div>
  )
}

function resolveWecomEnableBlocker(
  t: TFunction<'adminApps'>,
  identityReadiness: AppImIdentityReadiness | null,
  callbackReadiness: AppImSetupManifest['callback_readiness'] | null,
  aggregateReadiness: AppImSetupManifest['readiness'],
) {
  if (identityReadiness?.status === 'blocked') {
    const blockedItem = Object.entries(identityReadiness.items).find(
      ([, item]) => item.status === 'blocked',
    )
    if (blockedItem) {
      return t(`imDrawer.appStatus.wecom.identity.${blockedItem[0]}`, {
        defaultValue: t('imDrawer.appStatus.wecom.blocked'),
      })
    }
    return t('imDrawer.appStatus.wecom.blocked')
  }
  if (callbackReadiness?.status === 'blocked') {
    return t('imDrawer.appStatus.wecom.callbackBlocked')
  }
  return t(`imDrawer.appStatus.wecom.${aggregateReadiness}`)
}

function SectionHelp({ content }: { content: string }) {
  return <HelpTooltip label={content}>{content}</HelpTooltip>
}

function SectionTitle({ children, help }: { children: ReactNode; help?: string }) {
  return (
    <div className="inline-flex w-fit items-center gap-1">
      <h3 className="text-sm font-bold text-[#374151]">{children}</h3>
      {help ? <SectionHelp content={help} /> : null}
    </div>
  )
}

function ValidationResult({
  platform,
  validation,
}: {
  platform: AppImPlatform
  validation: AppImValidationResult
}) {
  const { t } = useTranslation('adminApps')
  const toneClassName = validation.passed
    ? 'border-[#b7ebc6] bg-[#f0faf3] text-[#16823c]'
    : 'border-[#ffe0a3] bg-[#fff9ed] text-[#8a5a00]'

  return (
    <div className={cn('rounded-lg border px-4 py-3 text-sm leading-6', toneClassName)}>
      <div className="font-medium">
        {validation.passed ? t('imDrawer.validation.passed') : t('imDrawer.validation.failed')}
      </div>
      {platform !== 'wecom' && [...validation.errors, ...validation.warnings].length > 0 ? (
        <ul className="mt-2 space-y-1">
          {[...validation.errors, ...validation.warnings].map((item) => (
            <li key={`${item.field}-${item.message}`}>{item.message}</li>
          ))}
        </ul>
      ) : null}
      {platform === 'wecom' &&
      validation.identity_readiness &&
      validation.identity_readiness.status !== 'ready' ? (
        <p className="mt-2">
          {resolveWecomEnableBlocker(
            t,
            validation.identity_readiness,
            null,
            validation.identity_readiness.status,
          )}
        </p>
      ) : validation.identity_readiness && validation.identity_readiness.status !== 'ready' ? (
        <p className="mt-2">
          {t(`imDrawer.identityReadiness.description.${validation.identity_readiness.status}`)}
        </p>
      ) : null}
    </div>
  )
}

const identityReadinessKeys = [
  'identity_connector_supported',
  'profile_credential_valid',
  'profile_api_accessible',
  'email_field_permitted',
  'directory_scope_configured',
] as const

function IdentityReadinessPanel({
  isLoading,
  readiness,
}: {
  isLoading: boolean
  readiness: AppImIdentityReadiness | null
}) {
  const { t } = useTranslation('adminApps')
  const status = readiness?.status ?? 'unknown'
  const unresolved = identityReadinessKeys.filter(
    (key) => readiness?.items[key]?.status !== 'ready',
  )

  return (
    <div className="border-b border-[var(--color-divider)] py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-[#374151]">{t('imDrawer.identityReadiness.title')}</div>
        <Badge
          variant={status === 'ready' ? 'success' : status === 'blocked' ? 'danger' : 'warning'}
        >
          {isLoading
            ? t('imDrawer.identityReadiness.loading')
            : t(`imDrawer.identityReadiness.status.${status}`)}
        </Badge>
      </div>
      <p className="mt-1 text-xs leading-5 text-[var(--color-text-subtle)]">
        {t(`imDrawer.identityReadiness.description.${status}`)}
      </p>
      {!isLoading && unresolved.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs leading-5 text-[var(--color-text-muted)]">
          {unresolved.map((key) => (
            <li key={key}>
              {t(`imDrawer.identityReadiness.checks.${key}`)}：
              {t(
                `imDrawer.identityReadiness.${readiness?.items[key]?.status === 'blocked' ? 'actions' : 'unknown'}.${key}`,
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function ModeRadioRow({
  disabled,
  name,
  onChange,
  title,
  value,
}: {
  disabled: boolean
  name: string
  onChange: (value: AppImMode) => void
  title: string
  value: AppImMode
}) {
  const { t } = useTranslation('adminApps')

  return (
    <div className="flex min-h-[28px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-[#374151]">{title}</div>
      <div className="flex flex-wrap gap-5">
        {modeOptions.map((option) => (
          <label
            className={cn(
              'inline-flex min-h-8 cursor-pointer items-center gap-2 text-sm text-[var(--color-text)]',
              disabled ? 'cursor-not-allowed opacity-60' : null,
            )}
            key={option.value}
          >
            <input
              checked={value === option.value}
              className="size-4 accent-[var(--color-primary)]"
              disabled={disabled}
              name={name}
              onChange={() => onChange(option.value)}
              type="radio"
              value={option.value}
            />
            {t(option.translationKey)}
          </label>
        ))}
      </div>
    </div>
  )
}

function WhitelistTextarea({
  disabled,
  label,
  onChange,
  placeholder,
  value,
}: {
  disabled: boolean
  label: string
  onChange: (value: string) => void
  placeholder: string
  value: string
}) {
  return (
    <label className="mt-4 grid gap-2">
      <span className="text-sm text-[#374151]">{label}</span>
      <textarea
        className="min-h-20 w-full resize-y rounded-lg border border-[var(--color-control-border)] bg-white px-3 py-2 text-sm text-[var(--color-text)] shadow-[var(--shadow-control)] outline-none transition-colors placeholder:text-[var(--color-control-placeholder)] hover:border-[var(--color-control-border-hover)] focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-soft)] disabled:cursor-not-allowed disabled:border-[var(--color-panel-border)] disabled:bg-[var(--color-surface-muted)] disabled:opacity-70 disabled:shadow-none"
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        value={value}
      />
    </label>
  )
}
